import { useCallback } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";

/** Per-account high-water mark of every nonce this browser has issued. */
const storageKey = (accountId: string) => `sugar-rush:nonce-hwm:${accountId}`;

function readHighWaterMark(accountId: string): number {
  try {
    const raw = localStorage.getItem(storageKey(accountId));
    const parsed = raw === null ? 0 : Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    // Private-mode or blocked storage: fall back to the timestamp floor below.
    return 0;
  }
}

function writeHighWaterMark(accountId: string, value: number): void {
  try {
    localStorage.setItem(storageKey(accountId), String(value));
  } catch {
    // Losing the mark costs monotonicity across reloads, not correctness --
    // the timestamp floor still clears anything already spent.
  }
}

/**
 * Returns a function that produces the next nonce for L2 transactions.
 *
 * The ledger's only rule is that a nonce must exceed the account's current
 * one (`account.nonce >= nonce` is rejected). Gaps cost nothing, so this
 * takes a millisecond timestamp as the floor instead of counting up from the
 * account view.
 *
 * Seeding from the view alone is what strands accounts. The view is built
 * from the ledger's event stream, so any action that advances the nonce
 * without emitting an event leaves it behind for good. A client counting
 * `view + 1` then proposes a value the ledger has already passed, and every
 * retry -- and every reload, which re-seeds from the same view -- repeats it.
 * A timestamp floor clears such a gap on the first attempt, whatever opened
 * it.
 *
 * The high-water mark is per-account and persisted, because a timestamp
 * floor alone is not monotonic: two hook instances called in the same
 * millisecond, or a clock stepped backwards by NTP, would otherwise reissue
 * a spent nonce. Keeping the mark in localStorage rather than a ref also
 * makes it shared -- a ref is per-instance, and every component calling this
 * hook gets its own.
 */
export function useNextNonce(): () => number | null {
  const { ledgerView } = useGummiwormContext();
  const myAccount = ledgerView.account;

  return useCallback(() => {
    if (!myAccount) return null;
    const accountId = myAccount.accountId;
    // Re-read on every call so a sibling tab's nonces are accounted for.
    const next = Math.max(
      readHighWaterMark(accountId) + 1,
      Number(myAccount.nonce) + 1,
      Date.now(),
    );
    writeHighWaterMark(accountId, next);
    return next;
  }, [myAccount]);
}
